/* TT Print Menu (Option 2) — Robust Injector (FULL FILE SURGERY)
   Goals (per Jeff):
   ✅ Fix missing Shopify line item properties (properties[...])
   ✅ Preserve ALL AVIS inputs (never touch .avis-input-hiddens)
   ✅ Reset TT fields ONLY after confirmed successful /cart/add
   ✅ Remove ALL timer-based and drawer-guess resets
   ✅ Clean, paste-ready replacement file (no partial edits)

   What this file does:
   - Injects [data-tt-pm-root] at TOP of the real /cart/add form
   - Fetches /pages/tt-print-menu-json?client=...&cat=...
   - Renders: Front tiles (f1–f8), Back (tiles if images else dropdown), Style tiles
   - Writes selections directly into Shopify line item properties:
       properties[Front Print], properties[Back Print], properties[Style]
   - Blocks submit if required selections missing (Front required only when front options exist)
   - Resets ONLY TT properties + TT UI AFTER successful add-to-cart is confirmed
*/

/* === TT_PM_FILE_START === */
(() => {
  const TT_PM = {
    DEBUG: false,
    ROOT_ATTR: "data-tt-pm-root",
    BOUND_ATTR: "data-tt-pm-bound",
    FORM_SELECTOR: 'form[action^="/cart/add"]',
    ENDPOINT_PATH: "/pages/tt-print-menu-json",

    // Shopify property names we own (ONLY these get reset)
    PROP_FRONT: "properties[Front Print]",
    PROP_BACK: "properties[Back Print]",
    PROP_STYLE: "properties[Style]",

    // Marker on the form while a submit attempt is in-flight
    PENDING_ATTR: "data-tt-pm-pending",
  };

  function log(...args) {
    if (TT_PM.DEBUG) console.log("[TT PM]", ...args);
  }
  function warn(...args) {
    console.warn("[TT PM]", ...args);
  }

  function qs() {
    return new URLSearchParams(window.location.search || "");
  }

  function getParamAny(keys) {
    const p = qs();
    for (const k of keys) {
      const v = (p.get(k) || "").trim();
      if (v) return v;
    }
    return "";
  }

  function findAddToCartForm() {
    const forms = Array.from(document.querySelectorAll(TT_PM.FORM_SELECTOR));
    if (!forms.length) return null;
    if (forms.length === 1) return forms[0];

    // Score likely product form
    const scored = forms
      .map((f) => {
        const hasVariantId = !!f.querySelector('input[name="id"]');
        const hasSubmit = !!f.querySelector('[type="submit"]');
        const inProductForm = !!f.closest("product-form");
        const score = (hasVariantId ? 10 : 0) + (hasSubmit ? 5 : 0) + (inProductForm ? 3 : 0);
        return { f, score };
      })
      .sort((a, b) => b.score - a.score);

    return scored[0]?.f || forms[0];
  }

  /* =========================================================
     TT_PM_HARD_RESET_START
     Prevent client/category bleed when navigating between products.
     - Remove previously injected UI roots
     - Clear "bound" markers so init can run again
     NOTE: This does NOT touch any form inputs.
     ========================================================= */
  function ttPmHardResetUIOnly() {
    try {
      document.querySelectorAll("[" + TT_PM.ROOT_ATTR + "]").forEach((el) => el.remove());
      document.querySelectorAll("[" + TT_PM.BOUND_ATTR + "]").forEach((el) =>
        el.removeAttribute(TT_PM.BOUND_ATTR)
      );
    } catch (e) {}
  }
  /* TT_PM_HARD_RESET_END */

  /* =========================================
     TT_PRINT_MENU_ENDPOINT_BUILDER
  ========================================= */
  function buildEndpointUrl(client, category) {
    if (!client || !category) {
      warn("TT Print Menu: missing client or category");
      return "";
    }
    return `${TT_PM.ENDPOINT_PATH}?client=${encodeURIComponent(client)}&cat=${encodeURIComponent(category)}`;
  }

  /* =========================================================
     TT_NORMALIZE_PRINT_MENU_PAYLOAD
  ========================================================= */
  function normalizeMenuPayload(json) {
    if (!json || json.ok !== true) return null;

    // Endpoint returns { ok:true, menu:{ front, back, style } }
    const raw = json.menu && typeof json.menu === "object" ? json.menu : json;

    const front = raw.front && typeof raw.front === "object" ? raw.front : {};
    const back = raw.back && typeof raw.back === "object" ? raw.back : {};
    const style =
      raw.style && (typeof raw.style === "object" || Array.isArray(raw.style)) ? raw.style : {};

    return { front, back, style };
  }

  function hasAnyOptions(node) {
    if (!node) return false;

    if (Array.isArray(node)) {
      return node.some((v) => !!v);
    }
    if (typeof node === "object") {
      return Object.keys(node).some((k) => !!node[k]);
    }
    return false;
  }

  function ensureRoot(formEl) {
    const existing = formEl.querySelector(`[${TT_PM.ROOT_ATTR}]`);
    if (existing) return existing;

    const root = document.createElement("div");
    root.setAttribute(TT_PM.ROOT_ATTR, "");
    root.className = "tt-pm";
    root.style.cssText = "margin:14px 0;";

    // Insert at TOP of form
    if (formEl.firstElementChild) formEl.insertBefore(root, formEl.firstElementChild);
    else formEl.appendChild(root);

    log("Inserted root at top of form.");
    return root;
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function escapeHtmlAttr(s) {
    return escapeHtml(s).replace(/`/g, "&#96;");
  }

  /* =========================================================
     TT_PM_RENDER_FRONT_START (CLEAN)
  ========================================================= */
  function renderFrontTiles(frontObj) {
    const keys = ["f1", "f2", "f3", "f4", "f5", "f6", "f7", "f8"];
    const tiles = [];

    for (const k of keys) {
      const v = frontObj[k];
      if (!v) continue;

      let label = `Front Print ${k.slice(1)}`;
      let img = "";

      if (typeof v === "string") {
        if (/^https?:\/\//i.test(v) || v.startsWith("//") || v.includes("/cdn/")) img = v;
        else label = v;
      } else if (typeof v === "object") {
        if (v.label) label = v.label;
        if (v.img) img = v.img;
        if (!img && v.img && typeof v.img === "object" && v.img.src) img = v.img.src;
      }

      tiles.push({ key: k, label, img });
    }

    if (!tiles.length) return "";

    return `
      <div class="tt-pm-section">
        <div style="font:700 14px/1.2 system-ui;margin:0 0 10px;color:#111;">SELECT FRONT PRINT</div>

        <!-- TT_PM_FRONT_TILES_START -->
        <div class="tt-pm-grid" data-tt-pm-front-grid>
          ${tiles
            .map(
              (t) => `
            <button
              type="button"
              class="tt-pm-tile tt-pm-tile--front"
              data-tt-pm-front="${escapeHtmlAttr(t.key)}"
              data-tt-pm-front-label="${escapeHtmlAttr(t.label)}"
              aria-pressed="false">
              <div class="tt-pm-tile-imgwrap">
                ${
                  t.img
                    ? `<img src="${escapeHtmlAttr(t.img)}" alt="${escapeHtmlAttr(
                        t.label
                      )}" loading="lazy" />`
                    : `<div class="tt-pm-tile-empty">No image</div>`
                }
              </div>
              <div class="tt-pm-tile-label">${escapeHtml(t.label)}</div>
            </button>
          `
            )
            .join("")}
        </div>
        <!-- TT_PM_FRONT_TILES_END -->
      </div>
    `;
  }
  /* =========================================================
     TT_PM_RENDER_FRONT_END (CLEAN)
  ========================================================= */

  /* =========================================================
     TT_PM_RENDER_BACK_START
  ========================================================= */
  function renderBack(backObj) {
    const keys = ["b1", "b2", "b3", "b4", "b5", "b6", "b7", "b8"];
    const opts = [];

    for (const k of keys) {
      const v = backObj && backObj[k];
      if (!v) continue;

      let label = `Back ${k.slice(1)}`;
      let img = "";

      if (typeof v === "string") {
        label = v;
      } else if (typeof v === "object") {
        if (v.label) label = v.label;
        if (v.img) img = v.img;
      }

      opts.push({ key: k, label, img });
    }

    if (!opts.length) return "";

    const allHaveImgs = opts.every((o) => !!o.img);

    if (!allHaveImgs) {
      const optionsHtml = [
        `<option value="">Choose a back print (optional)</option>`,
        ...opts.map((o) => `<option value="${escapeHtmlAttr(o.label)}">${escapeHtml(o.label)}</option>`),
      ].join("");

      return `
        <div class="tt-pm-section" style="margin-bottom:14px;">
          <div style="font:700 14px/1.2 system-ui;margin:0 0 10px;color:#111;">Select Back Print</div>
          <select class="tt-pm-back" data-tt-pm-back-select
            style="width:100%;padding:10px 12px;border:1px solid #ddd;border-radius:10px;font:600 14px/1.2 system-ui;">
            ${optionsHtml}
          </select>
        </div>
      `;
    }

    return `
      <div class="tt-pm-section" style="margin-bottom:14px;">
        <div style="font:700 14px/1.2 system-ui;margin:0 0 10px;color:#111;">CHOOSE YOUR DESIGN</div>

        <!-- TT_PM_BACK_TILES_START -->
        <div class="tt-pm-grid tt-pm-grid--back" data-tt-pm-back-grid>
          ${opts
            .map(
              (o) => `
            <button
              type="button"
              class="tt-pm-tile tt-pm-tile--back"
              data-tt-pm-back-tile="1"
              data-value="${escapeHtmlAttr(o.label)}"
              aria-pressed="false">
              <div class="tt-pm-tile-imgwrap">
                <img src="${escapeHtmlAttr(o.img)}" alt="${escapeHtmlAttr(o.label)}" loading="lazy" />
              </div>
              <div class="tt-pm-tile-label">${escapeHtml(o.label)}</div>
            </button>
          `
            )
            .join("")}
        </div>
        <!-- TT_PM_BACK_TILES_END -->
      </div>
    `;
  }
  /* =========================================================
     TT_PM_RENDER_BACK_END
  ========================================================= */

  /* =========================================================
     TT_PM_RENDER_STYLE_TILES_START (CLEAN)
  ========================================================= */
  function renderStyleTiles(styleNode) {
    const tiles = [];

    // Object keyed (s1..s8)
    if (styleNode && typeof styleNode === "object" && !Array.isArray(styleNode)) {
      const keys = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"];

      for (const k of keys) {
        const v = styleNode[k];
        if (!v) continue;

        let label = `Style ${k.slice(1)}`;
        let img = "";

        if (typeof v === "string") {
          if (/^https?:\/\//i.test(v) || v.startsWith("//") || v.includes("/cdn/")) img = v;
          else label = v;
        } else if (typeof v === "object") {
          if (v.label) label = v.label;
          else if (v.name) label = v.name;

          if (v.img) img = v.img;
          if (!img && v.img && typeof v.img === "object" && v.img.src) img = v.img.src;
          if (!img && v.image) img = v.image;
          if (!img && v.src) img = v.src;
        }

        tiles.push({ key: k, label, img });
      }
    }

    // Array form (up to 8)
    if (Array.isArray(styleNode)) {
      styleNode.slice(0, 8).forEach((v, i) => {
        if (!v) return;

        const key = `s${i + 1}`;
        let label = `Style ${i + 1}`;
        let img = "";

        if (typeof v === "string") {
          if (/^https?:\/\//i.test(v) || v.startsWith("//") || v.includes("/cdn/")) img = v;
          else label = v;
        } else if (typeof v === "object") {
          if (v.label) label = v.label;
          else if (v.name) label = v.name;

          if (v.img) img = v.img;
          if (!img && v.img && typeof v.img === "object" && v.img.src) img = v.img.src;
          if (!img && v.image) img = v.image;
          if (!img && v.src) img = v.src;
        }

        tiles.push({ key, label, img });
      });
    }

    if (!tiles.length) return "";

    return `
      <div class="tt-pm-section">
        <div class="tt-pm-title" style="font:700 14px/1.2 system-ui;margin:0 0 10px;color:#111;">Select Style</div>

        <!-- TT_STYLE_HIDDEN_INPUT_START -->
        <!-- NOTE: This hidden is UI-local; actual Shopify property is written on select -->
        <input type="hidden" value="" data-tt-pm-style-hidden>
        <!-- TT_STYLE_HIDDEN_INPUT_END -->

        <!-- TT_PM_STYLE_TILES_START -->
        <div class="tt-pm-grid" data-tt-pm-style-grid>
          ${tiles
            .map(
              (t) => `
            <button
              type="button"
              class="tt-pm-tile tt-pm-tile--style"
              data-tt-pm-style="${escapeHtmlAttr(t.label)}"
              aria-pressed="false">
              <div class="tt-pm-tile-imgwrap">
                ${
                  t.img
                    ? `<img src="${escapeHtmlAttr(t.img)}" alt="${escapeHtmlAttr(
                        t.label
                      )}" loading="lazy" />`
                    : `<div class="tt-pm-tile-empty">No image</div>`
                }
              </div>
              <div class="tt-pm-tile-label">${escapeHtml(t.label)}</div>
            </button>
          `
            )
            .join("")}
        </div>
        <!-- TT_PM_STYLE_TILES_END -->
      </div>
    `;
  }
  /* =========================================================
     TT_PM_RENDER_STYLE_TILES_END (CLEAN)
  ========================================================= */

  /* =========================================================
     TT_PM_RENDER_UI_START
  ========================================================= */
  function renderUI(rootEl, menu) {
    const hasFront = hasAnyOptions(menu.front);
    const hasBack = hasAnyOptions(menu.back);
    const hasStyle = hasAnyOptions(menu.style);

    rootEl.setAttribute("data-tt-pm-has-front", hasFront ? "1" : "0");
    rootEl.setAttribute("data-tt-pm-has-back", hasBack ? "1" : "0");
    rootEl.setAttribute("data-tt-pm-has-style", hasStyle ? "1" : "0");

    if (!hasFront && !hasBack && !hasStyle) {
      rootEl.innerHTML = "";
      log("No front/back/style options; leaving root empty.");
      return;
    }

    rootEl.innerHTML = `
      <div class="tt-pm-wrap" style="border:1px solid #eee;border-radius:14px;padding:14px 14px 10px;background:#fff;">
        <div class="tt-pm-error" data-tt-pm-error
          style="display:none;margin:0 0 10px;padding:10px 12px;border:1px solid #f2b8b5;background:#fdecea;border-radius:12px;font:700 13px/1.2 system-ui;color:#b42318;">
          Please complete required selections before adding to cart.
        </div>

        ${hasStyle ? renderStyleTiles(menu.style) : ""}
        ${hasFront ? renderFrontTiles(menu.front) : ""}
        ${hasBack ? renderBack(menu.back) : ""}
      </div>
    `;

    log("UI rendered. hasFront=", hasFront, "hasBack=", hasBack, "hasStyle=", hasStyle);
  }
  /* =========================================================
     TT_PM_RENDER_UI_END
  ========================================================= */

  /* =========================================================
     TT_PM_LINE_ITEM_PROPERTIES
     Fix missing Shopify properties by ensuring hidden inputs exist
     and ALWAYS writing selections into these exact inputs.
     ========================================================= */
  function ensurePropertyInput(formEl, propName) {
    // propName is already like "properties[Front Print]"
    let input = formEl.querySelector(`input[name="${CSS.escape(propName)}"]`);
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = propName;
      input.setAttribute("data-tt-pm-prop", "1");
      formEl.appendChild(input);
    }
    return input;
  }

  function getRoot(formEl) {
    return formEl.querySelector(`[${TT_PM.ROOT_ATTR}]`);
  }

  function hasFlag(formEl, flagName) {
    const root = getRoot(formEl);
    if (!root) return false;
    return root.getAttribute(flagName) === "1";
  }

  function hasFrontUI(formEl) {
    return hasFlag(formEl, "data-tt-pm-has-front");
  }
  function hasBackUI(formEl) {
    return hasFlag(formEl, "data-tt-pm-has-back");
  }
  function hasStyleUI(formEl) {
    return hasFlag(formEl, "data-tt-pm-has-style");
  }

  function setError(formEl, show, msg) {
    const root = getRoot(formEl);
    if (!root) return;
    const err = root.querySelector("[data-tt-pm-error]");
    if (!err) return;

    if (show) {
      err.textContent = msg || "Please complete required selections to continue.";
      err.style.display = "block";
    } else {
      err.style.display = "none";
    }
  }

  function TT_PM_getFrontLabelFromBtn(btn, fallbackKey) {
    if (!btn) return String(fallbackKey || "").trim();

    const dataLabel =
      (btn.getAttribute("data-tt-pm-front-label") || "").trim() ||
      (btn.getAttribute("data-label") || "").trim();

    if (dataLabel) return dataLabel;

    const labelEl = btn.querySelector(".tt-pm-tile-label,[data-tt-pm-label]");
    const textLabel = labelEl ? (labelEl.textContent || "").trim() : "";
    if (textLabel) return textLabel;

    return String(fallbackKey || "").trim();
  }

  function clearSelectedVisual(formEl, selector) {
    formEl.querySelectorAll(selector).forEach((b) => {
      b.classList.remove("is-active");
      b.style.outline = "";
      b.setAttribute("aria-pressed", "false");
    });
  }

  function activateTile(btn) {
    if (!btn) return;
    btn.classList.add("is-active");
    btn.style.outline = "3px solid #ff7a00";
    btn.setAttribute("aria-pressed", "true");
  }

  function validateRequired(formEl, { scroll } = { scroll: false }) {
    const root = getRoot(formEl);

    const frontInput = ensurePropertyInput(formEl, TT_PM.PROP_FRONT);
    const backInput = ensurePropertyInput(formEl, TT_PM.PROP_BACK);
    const styleInput = ensurePropertyInput(formEl, TT_PM.PROP_STYLE);

    // Front required ONLY if front options exist
    const frontOk = !hasFrontUI(formEl) || !!(frontInput.value || "").trim();

    // Back optional always (but if you ever make it required later, this line is ready)
    const backOk = true || !hasBackUI(formEl) || true;

    // Style optional unless style options exist AND you want it required (currently optional)
    const styleOk = true || !hasStyleUI(formEl) || true;

    const ok = frontOk && backOk && styleOk;

    if (!ok) {
      setError(formEl, true, "Please complete required selections before adding to cart.");
      if (scroll && root) root.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      setError(formEl, false);
    }

    return ok;
  }

  /* =========================================================
     TT_PM_RESET_AFTER_SUCCESS
     - Only clears TT properties (Front/Back/Style)
     - Never touches AVIS inputs (or any other properties)
     ========================================================= */
  function resetAfterSuccessfulAdd(formEl, reason) {
    try {
      const frontInput = ensurePropertyInput(formEl, TT_PM.PROP_FRONT);
      const backInput = ensurePropertyInput(formEl, TT_PM.PROP_BACK);
      const styleInput = ensurePropertyInput(formEl, TT_PM.PROP_STYLE);

      frontInput.value = "";
      backInput.value = "";
      styleInput.value = "";

      // Clear UI selection states
      clearSelectedVisual(formEl, "[data-tt-pm-front]");
      clearSelectedVisual(formEl, "[data-tt-pm-back-tile]");
      clearSelectedVisual(formEl, "[data-tt-pm-style]");

      // Clear UI-local hidden (if present)
      const root = getRoot(formEl);
      const styleHidden = root ? root.querySelector("[data-tt-pm-style-hidden]") : null;
      if (styleHidden) styleHidden.value = "";

      // Clear errors
      setError(formEl, false);

      log("Reset after successful add:", reason || "");
    } catch (e) {
      console.error("[TT PM] resetAfterSuccessfulAdd error:", e);
    }
  }

  /* =========================================================
     TT_PM_SUCCESS_DETECTION (NO TIMERS, NO DRAWER GUESS)
     We reset ONLY after we confirm /cart/add succeeded.
     We do this by:
     - marking the form pending on submit capture
     - intercepting fetch/XMLHttpRequest to detect /cart/add response ok
     - resetting TT fields for the pending form only
     ========================================================= */

  let ttPmFetchPatched = false;
  let ttPmXhrPatched = false;

  function isCartAddUrl(url) {
    try {
      const u = String(url || "");
      return u.includes("/cart/add");
    } catch (e) {
      return false;
    }
  }

  function markFormPending(formEl) {
    try {
      formEl.setAttribute(TT_PM.PENDING_ATTR, "1");
      formEl.setAttribute("data-tt-pm-pending-ts", String(Date.now()));
    } catch (e) {}
  }

  function clearFormPending(formEl) {
    try {
      formEl.removeAttribute(TT_PM.PENDING_ATTR);
      formEl.removeAttribute("data-tt-pm-pending-ts");
    } catch (e) {}
  }

  function findPendingForm() {
    return document.querySelector(`${TT_PM.FORM_SELECTOR}[${TT_PM.PENDING_ATTR}="1"]`);
  }

  function patchFetchForSuccess() {
    if (ttPmFetchPatched) return;
    ttPmFetchPatched = true;

    const origFetch = window.fetch;
    if (typeof origFetch !== "function") return;

    window.fetch = function patchedFetch(input, init) {
      const url = typeof input === "string" ? input : (input && input.url) || "";
      const method = (init && init.method) || (input && input.method) || "GET";

      const isAdd = isCartAddUrl(url) && String(method).toUpperCase() === "POST";

      // Call through first
      const p = origFetch.apply(this, arguments);

      if (!isAdd) return p;

      return p
        .then((resp) => {
          // Only act if some form is pending (set on submit capture)
          const formEl = findPendingForm();
          if (!formEl) return resp;

          // If response not ok, do nothing
          if (!resp || !resp.ok) {
            clearFormPending(formEl);
            return resp;
          }

          // Try to confirm success by reading JSON safely (clone)
          const clone = resp.clone();
          return clone
            .json()
            .then((json) => {
              // Shopify /cart/add.js usually returns the added line item object on success
              // We treat "has an id/variant_id/key" as success.
              const looksOk =
                !!json &&
                (json.id != null ||
                  json.variant_id != null ||
                  json.key != null ||
                  json.items != null ||
                  json.status === 200);

              if (looksOk) {
                resetAfterSuccessfulAdd(formEl, "fetch:/cart/add ok");
              }

              clearFormPending(formEl);
              return resp;
            })
            .catch(() => {
              // If JSON parse fails, still treat ok response as success.
              resetAfterSuccessfulAdd(formEl, "fetch:/cart/add ok (no json)");
              clearFormPending(formEl);
              return resp;
            });
        })
        .catch((err) => {
          // Failed network; clear pending but do not reset
          const formEl = findPendingForm();
          if (formEl) clearFormPending(formEl);
          throw err;
        });
    };

    log("Patched window.fetch for /cart/add success detection.");
  }

  function patchXhrForSuccess() {
    if (ttPmXhrPatched) return;
    ttPmXhrPatched = true;

    const XHR = window.XMLHttpRequest;
    if (!XHR) return;

    const origOpen = XHR.prototype.open;
    const origSend = XHR.prototype.send;

    XHR.prototype.open = function (method, url) {
      try {
        this.__ttPm_isAdd = isCartAddUrl(url) && String(method).toUpperCase() === "POST";
      } catch (e) {
        this.__ttPm_isAdd = false;
      }
      return origOpen.apply(this, arguments);
    };

    XHR.prototype.send = function () {
      if (this.__ttPm_isAdd) {
        const xhr = this;

        const onDone = function () {
          try {
            const formEl = findPendingForm();
            if (!formEl) return;

            // 2xx is considered success
            const ok = xhr.status >= 200 && xhr.status < 300;
            if (ok) {
              resetAfterSuccessfulAdd(formEl, "xhr:/cart/add ok");
            }
            clearFormPending(formEl);
          } catch (e) {}
        };

        xhr.addEventListener("load", onDone);
        xhr.addEventListener("error", () => {
          const formEl = findPendingForm();
          if (formEl) clearFormPending(formEl);
        });
        xhr.addEventListener("abort", () => {
          const formEl = findPendingForm();
          if (formEl) clearFormPending(formEl);
        });
      }

      return origSend.apply(this, arguments);
    };

    log("Patched XMLHttpRequest for /cart/add success detection.");
  }

  /* =========================================================
     BIND (ONCE)
  ========================================================= */
  function bindSelectionOnce(formEl) {
    if (formEl.getAttribute(TT_PM.BOUND_ATTR) === "1") {
      log("Form already bound; skipping event listener setup.");
      return;
    }
    formEl.setAttribute(TT_PM.BOUND_ATTR, "1");

    // Ensure our property inputs exist (THIS fixes missing Shopify line item properties)
    const frontInput = ensurePropertyInput(formEl, TT_PM.PROP_FRONT);
    const backInput = ensurePropertyInput(formEl, TT_PM.PROP_BACK);
    const styleInput = ensurePropertyInput(formEl, TT_PM.PROP_STYLE);

    // Back dropdown -> write directly to properties[Back Print]
    formEl.addEventListener(
      "change",
      (e) => {
        const sel = e.target && e.target.closest && e.target.closest("[data-tt-pm-back-select]");
        if (!sel) return;
        backInput.value = String(sel.value || "").trim();
        validateRequired(formEl, { scroll: false });
      },
      true
    );

    // Back tile clicks
    formEl.addEventListener(
      "click",
      (e) => {
        const btn = e.target && e.target.closest && e.target.closest("[data-tt-pm-back-tile]");
        if (!btn) return;

        e.preventDefault();

        const val = (btn.getAttribute("data-value") || "").trim();
        backInput.value = val;

        clearSelectedVisual(formEl, "[data-tt-pm-back-tile]");
        activateTile(btn);

        validateRequired(formEl, { scroll: false });
      },
      true
    );

    // Front tile clicks -> store HUMAN label in properties[Front Print]
    formEl.addEventListener(
      "click",
      (e) => {
        const btn = e.target && e.target.closest && e.target.closest("[data-tt-pm-front]");
        if (!btn) return;

        e.preventDefault();

        const key = (btn.getAttribute("data-tt-pm-front") || "").trim();
        if (!key) return;

        frontInput.value = TT_PM_getFrontLabelFromBtn(btn, key);

        clearSelectedVisual(formEl, "[data-tt-pm-front]");
        activateTile(btn);

        validateRequired(formEl, { scroll: false });

        log("Front selected:", key, "->", frontInput.value);
      },
      true
    );

    // Style tile clicks -> write to properties[Style]
    formEl.addEventListener(
      "click",
      (e) => {
        const btn = e.target && e.target.closest && e.target.closest("[data-tt-pm-style]");
        if (!btn) return;

        e.preventDefault();

        const label = (btn.getAttribute("data-tt-pm-style") || "").trim();
        if (!label) return;

        styleInput.value = label;

        // UI-local hidden (optional)
        const root = getRoot(formEl);
        const styleHidden = root ? root.querySelector("[data-tt-pm-style-hidden]") : null;
        if (styleHidden) styleHidden.value = label;

        clearSelectedVisual(formEl, "[data-tt-pm-style]");
        activateTile(btn);

        validateRequired(formEl, { scroll: false });

        log("Style selected:", label);
      },
      true
    );

    // HARD BLOCK submit until required selections are made (only when sections exist)
    formEl.addEventListener(
      "submit",
      (e) => {
        const ok = validateRequired(formEl, { scroll: true });
        if (!ok) {
          e.preventDefault();
          e.stopPropagation();
          warn("Blocked submit: required selections missing.");
          return;
        }

        // Mark pending so fetch/xhr success detector knows THIS submission is ours
        markFormPending(formEl);
      },
      true
    );

    // Initial validation
    validateRequired(formEl, { scroll: false });

    // Patch success detection once (global)
    patchFetchForSuccess();
    patchXhrForSuccess();
  }

  /* =========================================================
     INIT
  ========================================================= */
  async function init() {
    // UI-only reset for bfcache navigation
    ttPmHardResetUIOnly();

    try {
      const client = getParamAny(["client"]);
      const category = getParamAny(["category", "cat"]);

      log("Init. client=", client || "(blank)", "category=", category || "(blank)");

      const formEl = findAddToCartForm();
      if (!formEl) {
        warn("No /cart/add form found. Abort.");
        return;
      }

      const rootEl = ensureRoot(formEl);

      // Always bind once (so properties inputs exist even if menu empty)
      bindSelectionOnce(formEl);

      if (!client) {
        warn("Missing ?client=. Root inserted but leaving empty.");
        rootEl.innerHTML = "";
        rootEl.setAttribute("data-tt-pm-has-front", "0");
        rootEl.setAttribute("data-tt-pm-has-back", "0");
        rootEl.setAttribute("data-tt-pm-has-style", "0");
        return;
      }

      const url = buildEndpointUrl(client, category);
      log("Fetching endpoint:", url);

      if (!url) {
        rootEl.innerHTML = "";
        rootEl.setAttribute("data-tt-pm-has-front", "0");
        rootEl.setAttribute("data-tt-pm-has-back", "0");
        rootEl.setAttribute("data-tt-pm-has-style", "0");
        return;
      }

      const resp = await fetch(url, { credentials: "same-origin" });
      const json = await resp.json();

      log("Endpoint response:", json);

      if (!json || json.ok !== true) {
        warn("Endpoint ok!=true. Root inserted but leaving empty.");
        rootEl.innerHTML = "";
        rootEl.setAttribute("data-tt-pm-has-front", "0");
        rootEl.setAttribute("data-tt-pm-has-back", "0");
        rootEl.setAttribute("data-tt-pm-has-style", "0");
        return;
      }

      const menu = normalizeMenuPayload(json);
      if (!menu) {
        warn("Could not normalize menu payload.");
        rootEl.innerHTML = "";
        rootEl.setAttribute("data-tt-pm-has-front", "0");
        rootEl.setAttribute("data-tt-pm-has-back", "0");
        rootEl.setAttribute("data-tt-pm-has-style", "0");
        return;
      }

      renderUI(rootEl, menu);

      // Re-validate after UI render (so required logic sees has-front flags)
      validateRequired(formEl, { scroll: false });
    } catch (err) {
      console.error("[TT PM] Fatal init error:", err);
    }
  }

  function runWhenReady() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", init, { once: true });
    } else {
      init();
    }
  }

  runWhenReady();

  // bfcache recovery: safe because bindings are guarded by BOUND_ATTR
  window.addEventListener("pageshow", () => {
    init();
  });
})();
/* === TT_PM_FILE_END === */